import React from "react";
import Card from "@/components/ui/Card.jsx";

import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import PreferencesPageShell from "@/features/settings/preferences/PreferencesPageShell.jsx";
import AddChip from "@/sot/primitives/AddChip.jsx";
import supabase from "@/services/supabaseClient";
import DeleteCriticalIcon from "/icons/doctor/delete_critical.svg";
import ErrorIcon from "@/icons/afrimed/error.svg";
import TriangleAlertIcon from "@/icons/afrimed/triangle-alert.svg";

function TintedCard({ children, className = "", borderColor, background }) {
  return (
    <Card
      as="section"
      className={[
        "border p-4",
        className,
      ].join(" ")}
      style={{
        borderColor,
        background,
      }}
    >
      {children}
    </Card>
  );
}

function ToneChip({ label, palette, cardLike = false }) {
  const style = cardLike
    ? {
        background: palette.cardBg,
        color: palette.cardText,
        borderColor: palette.cardBorder,
      }
    : {
        background: palette.chipBg,
        color: palette.chipText,
        borderColor: palette.cardBorder,
      };

  return (
    <AddChip
      className="min-h-8 rounded-full border px-3 py-1 text-label-medium shadow-[var(--elev-1)] hover:opacity-100"
      style={style}
    >
      {label}
    </AddChip>
  );
}

function getTonePalette(tone) {
  if (tone === "danger") {
    return {
      sectionText: "text-[rgb(var(--clr-error))]",
      chipBg: "rgba(186,26,26,0.18)",
      chipText: "rgb(var(--schemes-critical))",
      cardBorder: "rgba(186,26,26,0.36)",
      cardBg: "rgba(186,26,26,0.16)",
      cardText: "rgb(var(--schemes-critical))",
    };
  }

  if (tone === "elevated") {
    return {
      sectionText: "text-[rgb(var(--clr-error-80))]",
      chipBg: "rgba(255,180,171,0.18)",
      chipText: "rgb(var(--schemes-critical))",
      cardBorder: "rgba(255,180,171,0.34)",
      cardBg: "rgba(255,180,171,0.14)",
      cardText: "rgb(var(--schemes-critical))",
    };
  }

  return {
    sectionText: "text-[rgb(var(--clr-on-surface))]",
    chipBg: "rgba(var(--clr-on-surface),0.08)",
    chipText: "rgb(var(--clr-on-surface-variant))",
    cardBorder: "rgba(var(--clr-outline),0.42)",
    cardBg: "rgba(var(--clr-on-surface),0.04)",
    cardText: "rgb(var(--clr-on-surface-variant))",
  };
}

function LifecycleStageSection({
  title,
  body,
  chip,
  tone = "warning",
  iconSrc = ErrorIcon,
  listItems = [],
  actionLabel = "",
  actionBusy = false,
  actionDisabled = false,
  actionStatus = "",
  onAction,
}) {
  const palette = getTonePalette(tone);

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <img src={iconSrc} alt="" className="mt-0.5 h-6 w-6 shrink-0 object-contain" />
          <div className="min-w-0">
            <div className="text-title-medium text-on-surface">{title}</div>
            <div className="mt-2 text-body-medium text-on-surface-var">{body}</div>
          </div>
        </div>
        <ToneChip label={chip} palette={palette} />
      </div>

      <TintedCard borderColor={palette.cardBorder} background={palette.cardBg}>
        <div className="space-y-4">
          {listItems.map((item) => (
            <div key={item.title} className="min-w-0">
              <div className="text-title-medium text-on-surface">{item.title}</div>
              <div className="mt-1 text-body-medium text-on-surface-var">{item.support}</div>
            </div>
          ))}
        </div>
      </TintedCard>

      <div>
        <AddChip
          disabled={actionBusy || actionDisabled}
          onClick={onAction}
          className="min-h-8 rounded-full border px-3 py-1 text-label-medium shadow-[var(--elev-1)] hover:opacity-100"
          style={{
            background: palette.cardBg,
            color: palette.cardText,
            borderColor: palette.cardBorder,
          }}
        >
          {actionBusy ? "Submitting..." : actionLabel}
        </AddChip>
        {actionStatus ? (
          <div className="mt-2 text-body-small text-on-surface-var" aria-live="polite">
            {actionStatus}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default function AccountLifecyclePage({ role = "patient" }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [busyAction, setBusyAction] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [error, setError] = React.useState("");
  const [latestByType, setLatestByType] = React.useState({});

  const loadLatestRequests = React.useCallback(async () => {
    if (!user?.id) return;
    const { data, error: loadError } = await supabase
      .from("account_lifecycle_requests")
      .select("id,request_type,status,requested_at,created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(12);
    if (loadError) return;
    const next = {};
    for (const row of Array.isArray(data) ? data : []) {
      const type = String(row?.request_type || "");
      if (type && !next[type]) next[type] = row;
    }
    setLatestByType(next);
  }, [user?.id]);

  React.useEffect(() => {
    void loadLatestRequests();
  }, [loadLatestRequests]);

  async function requestLifecycleAction(requestType) {
    if (busyAction) return;
    setBusyAction(requestType);
    setNotice("");
    setError("");
    try {
      const routeContext =
        typeof window !== "undefined"
          ? `${window.location.pathname || ""}${window.location.search || ""}${window.location.hash || ""}`
          : "";
      const { data, error: requestError } = await supabase.rpc(
        "request_account_lifecycle_action",
        {
          p_request_type: requestType,
          p_role: role,
          p_route_context: routeContext,
          p_message: null,
        }
      );
      if (requestError) throw requestError;
      const request = data?.request || {};
      setLatestByType((current) => ({
        ...current,
        [requestType]: request,
      }));
      setNotice(
        requestType === "pause"
          ? t(
              "settings.preferences.closeAccount.pauseSubmitted",
              "Account pause recorded. AfriMed™ support can reverse this after review."
            )
          : t(
              "settings.preferences.closeAccount.reviewSubmitted",
              "Request recorded for manual AfriMed™ support review."
            )
      );
    } catch (err) {
      setError(
        err?.message ||
          t(
            "settings.preferences.closeAccount.submitFailed",
            "We could not record this request. Please try again or contact support."
          )
      );
    } finally {
      setBusyAction("");
    }
  }

  const actionStatus = React.useCallback(
    (requestType) => {
      const row = latestByType[requestType];
      if (!row?.status) return "";
      const status = String(row.status).replace(/_/g, " ");
      return `Latest request: ${status} · ${new Date(row.requested_at || row.created_at).toLocaleString()}`;
    },
    [latestByType]
  );

  return (
    <PreferencesPageShell
      role={role}
      title={t(
        "settings.preferences.closeAccount.title",
        "Close, Pause or Delete Account"
      )}
      subtitle={t(
        "settings.preferences.closeAccount.subtitleLong",
        "We surface safer pauses and recovery-minded options first so you do not lose access by accident."
      )}
    >
      <div className="space-y-5">
        {notice ? (
          <div className="rounded-[12px] bg-[var(--state-primary-12)] px-4 py-3 text-body-medium text-on-surface-var">
            {notice}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
            {error}
          </div>
        ) : null}

        <LifecycleStageSection
          title={t("settings.preferences.closeAccount.pauseTitle", "Pause account access")}
          body={t(
            "settings.preferences.closeAccount.pauseBody",
            "Best for a temporary break. This should preserve your history while stopping routine activity."
          )}
          chip={t("settings.preferences.closeAccount.reversibleChip", "Reversible")}
          tone="warning"
          actionLabel={t("settings.preferences.closeAccount.pauseAction", "Pause account")}
          actionBusy={busyAction === "pause"}
          actionStatus={actionStatus("pause")}
          onAction={() => requestLifecycleAction("pause")}
          listItems={[
            {
              title: t("settings.preferences.closeAccount.pauseListOne", "History stays intact"),
              support: t(
                "settings.preferences.closeAccount.pauseListOneBody",
                "Preserve the account record while routine patient-facing activity pauses."
              ),
            },
            {
              title: t("settings.preferences.closeAccount.pauseListTwo", "Safer first step"),
              support: t(
                "settings.preferences.closeAccount.pauseListTwoBody",
                "Use this before closure or deletion when the need is temporary."
              ),
            },
          ]}
        />

        <LifecycleStageSection
          title={t("settings.preferences.closeAccount.closeTitle", "Close the account")}
          body={t(
            "settings.preferences.closeAccount.closeBody",
            "Best when you want to stop using AfriMed™ but still need a guided off-boarding path and record retention clarity."
          )}
          chip={t("common.reviewRequired", "Review required")}
          tone="elevated"
          iconSrc={TriangleAlertIcon}
          actionLabel={t("settings.preferences.closeAccount.closeAction", "Close account")}
          actionBusy={busyAction === "close"}
          actionStatus={actionStatus("close")}
          onAction={() => requestLifecycleAction("close")}
          listItems={[
            {
              title: t("settings.preferences.closeAccount.closeListOne", "Off-boarding review"),
              support: t(
                "settings.preferences.closeAccount.closeListOneBody",
                "We need to surface record retention, receipts, and compliance guidance before closure is final."
              ),
            },
            {
              title: t("settings.preferences.closeAccount.closeListTwo", "Recovery path matters"),
              support: t(
                "settings.preferences.closeAccount.closeListTwoBody",
                "Closure should stay less destructive than full deletion."
              ),
            },
          ]}
        />

        <LifecycleStageSection
          title={t("settings.preferences.closeAccount.deleteTitle", "Request deletion")}
          body={t(
            "settings.preferences.closeAccount.deleteBody",
            "This is the most sensitive option. AfriMed™ handles deletion requests manually so identity, billing, and health-related safeguards can be verified before permanent removal is completed."
          )}
          chip={t("common.reviewRequired", "Review required")}
          tone="danger"
          iconSrc={DeleteCriticalIcon}
          actionLabel={t("settings.preferences.closeAccount.deleteAction", "Request deletion")}
          actionBusy={busyAction === "deletion"}
          actionStatus={actionStatus("deletion")}
          onAction={() => requestLifecycleAction("deletion")}
          listItems={[
            {
              title: t("settings.preferences.closeAccount.deleteListOne", "How to request deletion"),
              support: t(
                "settings.preferences.closeAccount.deleteListOneBody",
                "Email info@afrimedtelehealth.com from the email linked to your AfriMed™ account. If email is not available, use AfriMed™ support channels and include enough identifying details for verification."
              ),
            },
            {
              title: t("settings.preferences.closeAccount.deleteListTwo", "Include these details"),
              support: t(
                "settings.preferences.closeAccount.deleteListTwoBody",
                "Full name, account email or phone number, role (patient or doctor), and the statement: “I request deletion of my AfriMed™ account data.”"
              ),
            },
          ]}
        />

        <section className="space-y-2">
          <div className="text-title-medium text-on-surface">
            {t(
              "settings.preferences.closeAccount.nextStepTitle",
              "Processing timeline"
            )}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {t(
              "settings.preferences.closeAccount.nextStepBody",
              "AfriMed™ acknowledges deletion requests and begins verification and processing as soon as reasonably possible. Completion timelines depend on identity verification and legal obligations, but requests are typically completed within 30 days."
            )}
          </div>
        </section>
      </div>
    </PreferencesPageShell>
  );
}
